/*
 * This takes an HTMLElement (the container) & truncates its text content on
 * word boundary at the end of the bounding box
 */
export class ReadMoreHelper {
  // container max height
  private maxHeight: number;

  constructor(
    private container: HTMLElement,
    private callback?: (isTruncated: boolean) => any,
  ) {}

  /**
   * Start the truncate process.
   * Takes the maximum line count before starting to truncate.
   */
  truncate(lineCount: number) {
    const button = this.container.querySelector('button.ReadMoreButton');
    if (button) {
      this.container.removeChild(button);
    }

    const lineHeight = getComputedStyle(this.container).getPropertyValue(
      'line-height',
    );

    this.maxHeight = parseFloat(lineHeight) * lineCount;

    // Truncate the text.
    if (!this.fits()) {
      // Append read more button
      const readMoreButton = document.createElement('button');
      readMoreButton.textContent = 'Read More';
      readMoreButton.classList.add('ReadMoreButton');
      readMoreButton.onclick = () => this.restore();
      this.container.append(readMoreButton);
      // Do truncate
      this.truncateToNode(this.container);
      if (this.callback) {
        this.callback(true);
      }
    }
  }

  /*
   * Truncate an element by removing elements from the end.
   */
  private truncateToNode(element: HTMLElement) {
    // replace all contents with comments
    const placeholders: CharacterData[] = [];
    const elements: Node[] = [];
    Array.prototype.slice.call(element.childNodes).forEach(node => {
      if (this.isTextNode(node)) {
        const placeholder = document.createTextNode('');
        node.replaceWith(placeholder);
        elements.push(node);
        placeholders.push(placeholder);
      }
    });

    if (!elements.length) {
      return;
    }

    // replace placeholders with contents until it doesn't fit anymore.
    let index;
    for (index = 0; index < elements.length; index++) {
      placeholders[index].replaceWith(elements[index]);
      const ellipsis = document.createTextNode('\u2026 ');
      const el = elements[index];
      if (this.isElementNode(el)) {
        el.append(ellipsis);
      } else if (this.isTextNode(el)) {
        el.after(ellipsis);
      }

      const fits = this.fits();
      (ellipsis.parentNode as Node).removeChild(ellipsis);

      if (!fits) {
        break;
      }
    }

    // The element that overflows.
    let lastElement =
      elements[Math.max(0, Math.min(index, elements.length - 1))];

    // The last node with only an ellipsis in it...
    if (this.isElementNode(lastElement)) {
      const newElement = document.createElement(lastElement.nodeName);
      newElement.append(document.createTextNode('\u2026 '));

      lastElement.replaceWith(newElement);

      if (this.fits()) {
        // Restore the full last element.
        newElement.replaceWith(lastElement);
      } else {
        // Remove it and go back one element.
        newElement.remove();
        lastElement = elements[Math.max(0, index - 1)];
      }
    }

    // Proceed inside last element.
    if (this.isElementNode(lastElement)) {
      this.truncateToNode(lastElement);
    } else if (this.isTextNode(lastElement)) {
      this.truncateToWord(lastElement);
    }
  }

  /*
   * Truncate a sentence by removing words from the end.
   */
  private truncateToWord(element: Text) {
    if (!element.textContent) {
      return;
    }

    const words = element.textContent.split(' ');

    if (words.filter(t => t).length < 2) {
      // If the sentence is a single word, fallback to letter-based truncation
      return this.truncateToLetter(element);
    }

    for (let a = 0; a <= words.length; a++) {
      element.textContent = this.addEllipsis(words.slice(0, a).join(' '));
      if (!this.fits()) {
        element.textContent = this.addEllipsis(words.slice(0, a - 1).join(' '));
        break;
      }
    }
  }

  /*
   * Truncate a word by removing letters from the end.
   */
  truncateToLetter(element: Text) {
    if (!element.textContent) {
      return;
    }

    const letters = element.textContent.split('');
    let text = '';

    for (let a = letters.length; a >= 0; a--) {
      text = letters.slice(0, a).join('');

      if (text.length) {
        element.textContent = this.addEllipsis(text);

        if (this.fits()) {
          break;
        }
      }
    }
  }

  /**
   * Test if the content fits in the container.
   */
  private fits(): boolean {
    return this.container.scrollHeight <= this.maxHeight;
  }

  /**
   * Add the ellipsis to a text.
   */
  private addEllipsis(text: string): string {
    const charsToRemove = [' ', ',', ';', '.', '!', '?'];
    let textCopy = text;

    while (charsToRemove.includes(textCopy.slice(-1))) {
      textCopy = textCopy.slice(0, -1);
    }
    textCopy += '\u2026 ';

    return textCopy;
  }

  /*
   * Restore the orignal content, i.e. expand the text
   */
  private restore() {
    const button = this.container.querySelector('button.ReadMoreButton');
    if (button) {
      this.container.removeChild(button);
    }
    if (this.callback) {
      this.callback(false);
    }
  }

  private isElementNode(node: Node): node is HTMLElement {
    return node.nodeType === Node.ELEMENT_NODE;
  }

  private isTextNode(node: Node): node is Text {
    return node.nodeType === Node.TEXT_NODE;
  }
}
